import {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponseHeaders,
  RawAxiosResponseHeaders,
} from "axios";

export interface CallsAlertsSearchBarValues {
  eventType?: EventType;
  status?: Status;
  accountId?: string;
  fromTradeDate: string | Date;
  toTradeDate: string | Date;
  toDueDate: string | Date;
  fromDueDate: string | Date;
  correspondentCode?: string[];
  callType?: string[];
  eventName?: string[];
  amountMinDisplay?: string;
  amountMinRaw?: number;
  amountMaxDisplay?: string;
  amountMaxRaw?: number;
}

export enum EventType {
  Null = "",
  Alert = "ALERT",
  Call = "CALL",
}

export enum Status {
  Open = "OPEN",
  Closed = "CLOSED",
  All = "",
}

// AKA CallType
export enum EventCode {
  Null = "",
  Pe = "PE",
  Gf = "GF",
  S1 = "S1",
  Em = "EM",
  Fr = "FR",
  Md = "MD",
  Cm = "CM",
  Rm = "RM",
  Rt = "RT",
  Dt = "DT", // Keep for API backwards compatibility
  Im = "IM", // New Intraday Margin code (FEECM-17670)
  Sl = "SL",
  Pm = "PM",
}

interface EventCodeLabelsAndValues {
  value: EventCode;
  label: string;
}

export const EventCodeLabels: {
  [key: string]: EventCodeLabelsAndValues;
} = {
  NULL: { value: EventCode.Null, label: "" },
  CM: { value: EventCode.Cm, label: "Concentrated Maintenance (CM)" },
  DT: { value: EventCode.Dt, label: "Day Trade Call (DT)" }, // Historical records (FEECM-17670 Req 5)
  EM: { value: EventCode.Em, label: "Equity Maintenance (EM)" },
  FR: { value: EventCode.Fr, label: "Free Ride Violation (FR)" },
  GF: { value: EventCode.Gf, label: "Good Faith Violations (GF)" },
  IM: { value: EventCode.Im, label: "Intraday Margin Call (IM)" }, // New records (FEECM-17670)
  MD: { value: EventCode.Md, label: "Money Due (MD)" },
  PE: { value: EventCode.Pe, label: "Portfolio Equity (PE)" },
  PM: { value: EventCode.Pm, label: "Portfolio Margin (PM)" },
  RT: { value: EventCode.Rt, label: "Reg T (RT)" },
  RM: { value: EventCode.Rm, label: "Required Maintenance Call (RM)" },
  SL: { value: EventCode.Sl, label: "Type LM Shorts (SL)" },
  S1: { value: EventCode.S1, label: "Type1 Shorts (S1)" },
};

export const CallTypes = {
  CM: "CM",
  DT: "DT",
  EM: "EM",
  FR: "FR",
  GF: "GF",
  IM: "IM", // Added for FEECM-17670 Req 5
  MD: "MD",
  PE: "PE",
  PM: "PM",
  RT: "RT",
  RM: "RM",
  SL: "SL",
  S1: "S1",
};

export interface GetCallsFromAPI {
  totalRows: number;
  records: CallDetailResultFromAPI[];
}
export interface CallDetailResultFromAPI {
  account: string;
  accountHolderName: string;
  callAmount: number;
  closedDate: string;
  correspondentCode: string;
  description: string;
  dueDate: string;
  eventCode: EventCode;
  eventId: string;
  eventName: string;
  eventType: EventType;
  issueDate: string;
  status: Status;
  tradeDate: string;
}

export type RecordField =
  | "actionMenu"
  | "status"
  | "correspondentCode"
  | "callId"
  | "type"
  | "accountId"
  | "accountHolder"
  | "callType"
  | "eventName"
  | "description"
  | "callAmount"
  | "tradeDate"
  | "dueDate"
  | "issueDate"
  | "closedDate";

export interface ExportToolSearchBarValues extends CallsAlertsSearchBarValues {}

export type Record = {
  actionMenu?: CallDetailResultFromAPI;
  key: string;
  status: Status;
  type: string;
  callId: string;
  correspondentCode: string;
  accountId: string;
  accountHolder: string;
  callType: EventCode;
  eventName: string;
  description: string;
  callAmount: number;
  tradeDate: string;
  dueDate: string;
  closedDate: string;
  issueDate: string;
};

export type NoteDetailsList = {
  actor: string;
  dateTime: string;
  description: string;
};

export type NotesData = {
  account: string;
  eventId: string;
  noteDetailsList: NoteDetailsList[];
};

export interface MarginProcessingError extends AxiosError {
  response: {
    status: number;
    data: {
      errorDescription: string;
      message: string;
      errorCode: string;
      error: string;
    };
    statusText: string;
    headers: RawAxiosResponseHeaders | AxiosResponseHeaders;
    config: AxiosRequestConfig;
  };
}
